import { useCallback, useEffect, useRef, useState } from 'react';
import '../styles/navbar/navbar.css';
import { captureEvent } from '../lib/analytics';
import { useScrollLock } from '../lib/smoothScroll';
import { goToSectionAnywhere } from '../lib/router';
import Logo from './Logo';

type Theme = 'light' | 'dark';

const PRIMARY_LINKS = [
    { id: 'about', label: 'Over mij' },
    { id: 'projects', label: 'Projecten' },
    { id: 'services', label: 'Diensten' },
    { id: 'contact', label: 'Contact' },
] as const;

/* While the menu is open, everything behind it is inert (also acts as the focus trap). */
const INERT_SELECTOR = '[data-menu-inert]';

/*
 * Studio X dock nav (schmitt-design: studiox-dock-nav). No header bar: a small
 * floating pill at the bottom centre. The logo sits in a circle, then a glass
 * pill with the menu button and the one accent call to action. The dock takes
 * on the theme (paper or ink) of whichever section is under it.
 */
const Navbar = () => {
    const [open, setOpen] = useState(false);
    const [theme, setTheme] = useState<Theme>('dark'); // the hero, a navy section, is first
    const menuButtonRef = useRef<HTMLButtonElement>(null);
    const firstLinkRef = useRef<HTMLAnchorElement>(null);

    useScrollLock(open);

    // Theme follows the section under the dock: the last section whose top is at or above 93.5% of the viewport.
    useEffect(() => {
        let frame = 0;
        const update = () => {
            frame = 0;
            const line = window.scrollY + window.innerHeight * 0.935;
            let current: Theme = 'light';
            document.querySelectorAll<HTMLElement>('section[data-theme]').forEach((section) => {
                const top = section.getBoundingClientRect().top + window.scrollY;
                if (top <= line) current = section.dataset.theme === 'dark' ? 'dark' : 'light';
            });
            setTheme(current);
        };
        const schedule = () => {
            if (!frame) frame = window.requestAnimationFrame(update);
        };

        window.addEventListener('scroll', schedule, { passive: true });
        window.addEventListener('resize', schedule);
        // Sections are lazy loaded: re-check as they arrive.
        const observer = new MutationObserver(schedule);
        observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-theme'] });
        update();

        return () => {
            window.removeEventListener('scroll', schedule);
            window.removeEventListener('resize', schedule);
            observer.disconnect();
            if (frame) window.cancelAnimationFrame(frame);
        };
    }, []);

    // Open menu: Escape closes, content behind goes inert, focus moves in and returns on close.
    useEffect(() => {
        if (!open) return;
        const toggle = menuButtonRef.current;
        const inert = document.querySelectorAll<HTMLElement>(INERT_SELECTOR);
        inert.forEach((el) => el.setAttribute('inert', ''));
        const focusTimer = window.setTimeout(() => firstLinkRef.current?.focus(), 80);
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false);
        };
        window.addEventListener('keydown', onKey);

        return () => {
            window.clearTimeout(focusTimer);
            window.removeEventListener('keydown', onKey);
            inert.forEach((el) => el.removeAttribute('inert'));
            toggle?.focus({ preventScroll: true });
        };
    }, [open]);

    const go = useCallback(
        (id: string, menu: 'dock' | 'menu') => (event: React.MouseEvent) => {
            event.preventDefault();
            captureEvent('nav_link_clicked', { section: id, menu });
            if (open) {
                setOpen(false);
                // Let the panel start leaving and the scroll lock release first.
                window.setTimeout(() => goToSectionAnywhere(id), 450);
            } else {
                goToSectionAnywhere(id);
            }
        },
        [open]
    );

    const dockTheme: Theme = open ? 'dark' : theme;

    return (
        <>
            <nav className="dock" data-theme={dockTheme} aria-label="Hoofdnavigatie">
                <a className="dock__logo" href="#hero" onClick={go('hero', 'dock')} aria-label="Schmitt Software, naar boven">
                    <Logo className="dock__mark" />
                </a>
                <div className="dock__pill">
                    <button
                        ref={menuButtonRef}
                        type="button"
                        className="dock__menu"
                        aria-expanded={open}
                        aria-controls="menu-panel"
                        onClick={() => setOpen((value) => !value)}
                    >
                        {open ? 'Sluit' : 'Menu'}
                    </button>
                    <a className="dock__cta" href="#contact" onClick={go('contact', 'dock')}>
                        Offerte aanvragen
                    </a>
                </div>
            </nav>

            <div id="menu-panel" className="menu" data-open={open} role="dialog" aria-modal="true" aria-label="Menu">
                <div className="menu__inner">
                    <ul className="menu__primary">
                        {PRIMARY_LINKS.map((link, index) => (
                            <li key={link.id} style={{ '--i': index } as React.CSSProperties}>
                                <a
                                    ref={index === 0 ? firstLinkRef : undefined}
                                    className="menu__link display"
                                    href={`#${link.id}`}
                                    onClick={go(link.id, 'menu')}
                                >
                                    {link.label}
                                </a>
                            </li>
                        ))}
                    </ul>
                    <ul className="menu__secondary">
                        <li>
                            <a className="link" href="#stats" onClick={go('stats', 'menu')}>
                                Waarom een website
                            </a>
                        </li>
                        <li>
                            <a className="link" href="tel:+32468345181">
                                +32 468 34 51 81
                            </a>
                        </li>
                        <li>
                            <a className="link" href="mailto:schmitt.software@gmail.com">
                                schmitt.software@gmail.com
                            </a>
                        </li>
                        <li>
                            <a className="link" href="/privacy-policy.php">
                                Privacybeleid
                            </a>
                        </li>
                        <li>
                            <a className="link" href="/terms-conditions.php">
                                Algemene voorwaarden
                            </a>
                        </li>
                    </ul>
                </div>
            </div>
        </>
    );
};

export default Navbar;
